import { useMutation, useQuery, UseQueryResult } from "@tanstack/react-query";
import {
  cardIssuanceStatusQueryConfig,
  ILimitResponse,
  isNetworkError,
  limitIdentificationEsiaMutationFn,
  limitsQueryConfig,
  paymentCardAvailabilityQueryConfig,
} from "../../../../../api";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RoutesMap } from "../../../../../routes.tsx";
import { useCardIssuanceMutationReaction } from "./useCardIssuanceMutationReaction.ts";
import { useCardIssuanceStatusReaction } from "./useCardIssuanceStatusReaction.ts";

const LIMIT_POLL_INTERVAL_MS = 10_000;

const LIMIT_STATUSES_STOP_POLLING = new Set<string>([
  // terminal / immediate redirects
  "ACTIVE",
  "BLOCKED",
  "CONFIRMED",
  "DECLINED_SPR",
  "DECLINED_TIMEOUT",
  "DECLINED",
  "WAITING_FOR_CONFIRMATION",
  // another flow takes over
  "WAITING_FOR_CARD_ISSUE",
  // special-case: triggers ESIA redirect (no polling needed)
  "NEW",
]);

const LIMIT_STATUSES_SHOW_PROCESSING_UI = new Set<string>([
  "SCORING_IN_PROGRESS",
  "WAITING_FOR_IDENTIFICATION",
  "CONFIRMATION_IN_PROGRESS",
]);

function shouldPollLimits(status: string | undefined) {
  if (!status) return false;
  return !LIMIT_STATUSES_STOP_POLLING.has(status);
}

function shouldShowProcessingUI(status: string | undefined) {
  return !!status && LIMIT_STATUSES_SHOW_PROCESSING_UI.has(status);
}

export type TLoadingPageView = "error" | "processing" | "loader";

export function useLoadingPageFlow() {
  const navigate = useNavigate();
  const lastHandledRef = useRef<string | null>(null);
  const limits = useQuery({
    ...limitsQueryConfig(),
    refetchInterval: (query) => {
      const { data } = query.state;
      const firstLimit = data?.[0];
      return shouldPollLimits(firstLimit?.status) ? LIMIT_POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: true,
  }) as UseQueryResult<ILimitResponse[]>;

  const esiaMutation = useMutation({
    mutationFn: limitIdentificationEsiaMutationFn,
  });

  const firstLimit = limits.data?.[0];
  const limitId = firstLimit?.id;
  const limitStatus = firstLimit?.status;

  const cardFlowEnabled = limitStatus === "WAITING_FOR_CARD_ISSUE";

  const cardIssuanceStatus = useQuery({
    ...cardIssuanceStatusQueryConfig(limitId ?? ""),
    enabled: cardFlowEnabled && !!limitId,
  });

  const hasError456 = cardIssuanceStatus.error
    && isNetworkError(cardIssuanceStatus.error)
    && cardIssuanceStatus.error.status === 456
    && !cardIssuanceStatus.data; // Only trigger card creation if we don't have data yet

  const paymentCardAvailability = useQuery({
    ...paymentCardAvailabilityQueryConfig(limitId ?? ""),
    enabled: cardFlowEnabled && !!hasError456 && !!limitId,
  });

  const shouldTriggerCardIssuance = paymentCardAvailability.isSuccess
    && paymentCardAvailability.data
    && !!limitId
    && !cardIssuanceStatus.data; // Don't create if we already have card status data

  const cardIssuanceMutation = useCardIssuanceMutationReaction(limitId, shouldTriggerCardIssuance);

  const shouldFetchStatusAfterMutation = cardFlowEnabled
    && !!limitId
    && (cardIssuanceMutation.isSuccess || cardIssuanceStatus.isSuccess);
  const cardIssuanceStatusAfterMutation = useCardIssuanceStatusReaction(limitId, shouldFetchStatusAfterMutation);

  const view: TLoadingPageView = useMemo(() => {
    if (limits.isError) return "error";

    // No limits yet (or empty list) -> keep showing loader.
    if (!firstLimit) return "loader";

    if (shouldShowProcessingUI(limitStatus)) return "processing";

    // Card issuance flow-specific blocking errors.
    if (cardFlowEnabled) {
      if (paymentCardAvailability.isSuccess && !paymentCardAvailability.data) return "error";
      if (paymentCardAvailability.isError) return "error";
      if (cardIssuanceMutation.isError) return "error";
      if (cardIssuanceStatus.data === "ERROR" || cardIssuanceStatusAfterMutation.data === "ERROR") return "error";
    }

    return "loader";
  }, [
    limits.isError,
    firstLimit,
    limitStatus,
    cardFlowEnabled,
    paymentCardAvailability.isSuccess,
    paymentCardAvailability.isError,
    paymentCardAvailability.data,
    cardIssuanceMutation.isError,
    cardIssuanceStatus.data,
    cardIssuanceStatusAfterMutation.data,
  ]);

  useEffect(() => {
    if (!limits.isSuccess) {
      return;
    }

    const firstLimit = limits.data[0] as ILimitResponse | undefined;

    if (!firstLimit) {
      return;
    }

    const handledKey = `${firstLimit.id}:${firstLimit.status}`;
    if (lastHandledRef.current === handledKey) {
      return;
    }
    lastHandledRef.current = handledKey;

    const handleLimitStatus = async () => {
      switch (firstLimit.status) {
        case "BLOCKED":
        case "CONFIRMED":
        case "ACTIVE": {
          navigate(RoutesMap.index);
          return;
        }
        case "NEW": {
          try {
            const redirectUrl = `${window.location.origin}${RoutesMap.index}`;
            const response = await esiaMutation.mutateAsync({ limitId: firstLimit.id, redirectUrl });
            window.location.href = response.url;
          } catch (error) {
            console.error("ESIA identification error:", error);
            navigate(RoutesMap.login.loading);
          }
          return;
        }
        case "SCORING_IN_PROGRESS":
        case "WAITING_FOR_IDENTIFICATION":
        case "CONFIRMATION_IN_PROGRESS": {
          // Show loading UI in LoadingPage component, don't navigate
          return;
        }
        case "DECLINED_SPR":
        case "DECLINED_TIMEOUT":
        case "DECLINED": {
          navigate(RoutesMap.login.limitReject);
          return;
        }
        case "WAITING_FOR_CONFIRMATION": {
          navigate(RoutesMap.login.limitSuccess);
          return;
        }
        case "WAITING_FOR_CARD_ISSUE": {
          // API call is handled by useQuery above, response will be logged
          return;
        }
        default: {
          // Unknown status: stay on this page and keep polling (see shouldPollLimits).
          return;
        }
      }
    };

    void handleLimitStatus();
  }, [limits.isSuccess, limits.data, navigate, esiaMutation]);

  return {
    view,
    limits,
    firstLimit,
    limitId,
    limitStatus,
    // Keeping these exposed for UI/tests.
    paymentCardAvailability,
    cardIssuanceMutation,
    cardIssuanceStatus,
    cardIssuanceStatusAfterMutation,
  };
}
